import cn from 'classnames'
import { EChartsOption } from 'echarts'
import ReactEcharts from 'echarts-for-react'
import { getFieldWithSellType } from 'legacy/lut/components/widgets/public/partner360/common/reports/quarterlyViewMarketTable/helpers/fieldsNameHelper'
import { TSobReportSalesType } from 'legacy/lut/components/widgets/public/partner360/configs/reports'
import {
  FINANCIAL_VALUE_FORMAT_OPTIONS,
  QUARTER,
  QUARTERS_IN_CHART,
} from 'legacy/lut/components/widgets/public/partner360/configs/reports'
import { getYearAndQuarters } from 'legacy/lut/components/widgets/public/partner360/reports/helpers/getYearAndQuarters'
import React, { FC, useCallback, useMemo } from 'react'

import { Nullable } from 'clickNgo/models/utils'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { getFormattedFinance } from 'cng-common/helpers/numbers'
import {
  ISobAnalyticsReportChartDataQuarter,
  ISobAnalyticsReportWeightedAverageNetNdp,
} from 'partner360/models/report/sobAnalyticsReports/ISobAnalyticsReportWeightedAverage'

import { TRANSLATION_KEYS } from '../translations'

import styles from './BarChart.module.scss'

interface IBarChartProps {
  className?: string
  selectedFYear: number
  currentQuarter: number
  withoutTitle?: boolean
  shouldShowFutureYears: boolean
  chartData: {
    quartersChartData: ISobAnalyticsReportChartDataQuarter[]
    yearChartsData: ISobAnalyticsReportWeightedAverageNetNdp[]
  } | null

  dataByYearLength?: number
  uniqByPartnersLength?: number
  uniqByPartnersWithCurrentActualsLength?: number
  chartTitleTranslationKey?: string
  sellType: TSobReportSalesType
  onChartClick?: (year: number, quarter: number | undefined) => void
}

export const BarChart: FC<IBarChartProps> = ({
  className,
  selectedFYear,
  withoutTitle,
  shouldShowFutureYears,
  chartTitleTranslationKey,
  chartData,
  dataByYearLength,
  uniqByPartnersLength,
  uniqByPartnersWithCurrentActualsLength,
  sellType,
  onChartClick,
  currentQuarter,
}) => {
  const currentYear = selectedFYear
  const prevYear = useMemo(() => selectedFYear - 1, [selectedFYear])

  const periodData = useMemo(() => {
    const periods = [
      ...getYearAndQuarters({
        currentFQuarter: QUARTER,
        currentFYear: currentYear,
        quartersBack: QUARTERS_IN_CHART,
        includeCurrentQuarter: true,
      }),
      { quarter: 'FY', year: prevYear },
      { quarter: 'FY', year: currentYear },
    ]

    if (shouldShowFutureYears) {
      return [
        ...periods,
        { quarter: 'FY', year: currentYear + 1 },
        { quarter: 'FY', year: currentYear + 2 },
      ]
    }

    return periods
  }, [currentYear, prevYear, shouldShowFutureYears])

  const option = useMemo<Nullable<EChartsOption>>(() => {
    const groupColors = periodData.map((period) => {
      if (period.quarter === 'FY') {
        return 'rgba(211, 211, 211, 0.3)'
      }
      return period.year === prevYear ? 'rgba(173, 216, 230, 0.3)' : 'rgba(240, 230, 140, 0.3)'
    })
    if (!chartData) {
      return null
    }

    const chartFontSize = 15
    const { quartersChartData, yearChartsData } = chartData

    const yearlyBars = Array.from(quartersChartData)
      .map((it) => ({
        ...it,
        plans: 0,
        actuals: 0,
        plansNet: 0,
        actualsNet: 0,
        plansTNet: 0,
        actualsTNet: 0,
        plansUTNet: 0,
        actualsUTNet: 0,
      }))
      .concat(yearChartsData)

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const value = params.data > 0 ? `${params.data.toFixed(1)}%` : ''
          return `${params.marker} ${params.seriesName}: ${value}`
        },
      },
      legend: {
        data: ['Plans', 'Actuals'],
        bottom: 0,
        left: 'center',
        textStyle: {
          fontSize: chartFontSize,
        },
      },
      grid: {
        bottom: 50,
        left: 30,
        top: 50,
        right: 20,
      },
      xAxis: {
        type: 'category',
        axisLabel: {
          fontSize: chartFontSize,
        },
        data: periodData.map((period) =>
          period.quarter === 'FY'
            ? `${period.quarter} ${String(period.year).slice(-2)}`
            : `Q${period.quarter} ${String(period.year).slice(-2)}`
        ),
        splitArea: {
          show: true,
          areaStyle: {
            color: groupColors,
          },
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: {
          fontSize: chartFontSize,
        },
      },
      series: [
        {
          name: 'Plans',
          type: 'line',
          data: quartersChartData.map((item) => {
            const field = getFieldWithSellType<ISobAnalyticsReportChartDataQuarter>(
              'plans',
              sellType
            )
            return item[field] || undefined
          }),
          itemStyle: {
            color: '#549EF7',
          },
          lineStyle: {
            type: 'dashed',
          },
          label: {
            show: true,
            position: 'top',
            fontSize: 15,
            formatter: (params: any) => {
              return params.value > 0
                ? `${getFormattedFinance(params.value, FINANCIAL_VALUE_FORMAT_OPTIONS)}`
                : ''
            },
          },
        },
        {
          name: 'Actuals',
          type: 'line',
          data: quartersChartData.map((item) => {
            const field = getFieldWithSellType<ISobAnalyticsReportChartDataQuarter>(
              'actuals',
              sellType
            )
            return item[field] || undefined
          }),

          itemStyle: {
            color: '#3DD779',
          },
          label: {
            show: true,
            position: 'bottom',
            fontSize: chartFontSize,
            formatter: (params: any) => {
              return params.value > 0
                ? `${getFormattedFinance(params.value, FINANCIAL_VALUE_FORMAT_OPTIONS)}`
                : ''
            },
          },
        },

        {
          name: 'Plans',
          type: 'bar',
          data: yearlyBars.map((item) => {
            const field = getFieldWithSellType<ISobAnalyticsReportWeightedAverageNetNdp>(
              'plans',
              sellType
            )
            return item[field] || undefined
          }),

          itemStyle: {
            color: '#549EF7',
          },
          label: {
            show: true,
            position: 'inside',
            fontSize: chartFontSize - 2,
            formatter: (params: any) => {
              return params.value > 0 ? `${params.value.toFixed(1)}%` : ''
            },
          },
        },
        {
          name: 'Actuals',
          type: 'bar',
          data: yearlyBars.map((item) => {
            const field = getFieldWithSellType<ISobAnalyticsReportWeightedAverageNetNdp>(
              'actuals',
              sellType
            )
            return item[field] || undefined
          }),

          itemStyle: {
            color: '#3DD779',
          },
          label: {
            show: true,
            position: 'inside',
            fontSize: chartFontSize - 2,
            formatter: (params: any) => {
              return params.value > 0 ? `${params.value.toFixed(1)}%` : ''
            },
          },
        },
      ],

      graphic: [
        {
          type: 'text',
          left: '4%',
          top: '5%',
          style: {
            text: `FY ${prevYear}`,
            font: 'bold 18px Arial',
            fill: '#909090',
          },
        },
        {
          type: 'text',
          left: '42%',
          top: '5%',
          style: {
            text: `FY ${currentYear}`,
            font: 'bold 18px Arial',
            fill: '#909090',
          },
        },
        {
          type: 'text',
          left: '80%',
          top: '5%',
          style: {
            text: shouldShowFutureYears
              ? `FY ${String(prevYear).slice(-2)}/${String(currentYear).slice(-2)}/${String(currentYear + 1).slice(-2)}/${String(currentYear + 2).slice(-2)}`
              : `FY ${String(prevYear).slice(-2)}/${String(currentYear).slice(-2)}`,
            font: 'bold 18px Arial',
            fill: '#909090',
          },
        },
      ],
    }
  }, [periodData, chartData, prevYear, currentYear, shouldShowFutureYears, sellType])

  const handleChartBarClick = useCallback(
    (barSettings: any) => {
      if (onChartClick) {
        const [quarterString, yearTwoDigits] = barSettings.name.split(' ')
        let quarter: number | undefined
        if (quarterString !== 'FY') {
          quarter = parseInt(quarterString.slice(-1))
        }
        const year = parseInt(`20${yearTwoDigits}`)
        onChartClick(year, quarter)
      }
    },
    [onChartClick]
  )

  if (!option) {
    return null
  }

  return (
    <div className={cn(styles.root, className)}>
      <RenderWhen condition={!withoutTitle}>
        <div className={styles.chartTitle}>
          <Translation text={TRANSLATION_KEYS.chart.barChartTitle} />
        </div>
      </RenderWhen>

      <RenderWhen condition={Boolean(chartTitleTranslationKey)}>
        <div className={styles.filteredCount}>
          <Translation
            text={chartTitleTranslationKey!}
            replaceParams={[
              { key: '{plans}', value: dataByYearLength ? String(dataByYearLength) : '-' },
              {
                key: '{partners}',
                value: uniqByPartnersLength ? String(uniqByPartnersLength) : '-',
              },
              {
                key: '{current-actuals}',
                value: uniqByPartnersWithCurrentActualsLength
                  ? String(uniqByPartnersWithCurrentActualsLength)
                  : '-',
              },
              { key: '{current-year}', value: String(currentYear % 2000) },
              { key: '{prev-year}', value: String(prevYear % 2000) },
              { key: '{current-quarter}', value: String(currentQuarter) },
            ]}
            htmlAvailable
          />
        </div>
      </RenderWhen>

      <ReactEcharts
        key={shouldShowFutureYears.toString()}
        option={option}
        style={{ width: '100%', height: '400px' }}
        onEvents={{ click: handleChartBarClick }}
      />
    </div>
  )
}
